import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../lib/adminService';
import { AdminNavigation } from '../../components/AdminNavigation';
import {
  DollarSign,
  // TrendingUp, // TODO: Add trending charts in future iteration
  CreditCard,
  ShoppingCart,
  Users,
  Calendar,
} from 'lucide-react';

export function AdminFinancials() {
  const [metrics, setMetrics] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    setLoading(true);
    try {
      const [metricsData, transactionsData] = await Promise.all([
        adminService.getFinancialMetrics(),
        adminService.getAllTransactions({ limit: 50 }),
      ]);
      setMetrics(metricsData);
      setTransactions(transactionsData.data || []);
    } catch (error) {
      console.error('Error loading financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavigation />
        <div className="min-h-screen flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </>
    );
  }

  const revenueCards = [
    {
      label: 'Total Revenue',
      value: `$${metrics?.totalRevenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'from-green-500 to-emerald-600',
    },
    {
      label: 'Subscription Revenue',
      value: `$${metrics?.subscriptionRevenue?.toFixed(2) || '0.00'}`,
      icon: Calendar,
      color: 'from-blue-500 to-cyan-600',
    },
    {
      label: 'Purchase Revenue',
      value: `$${metrics?.purchaseRevenue?.toFixed(2) || '0.00'}`,
      icon: ShoppingCart,
      color: 'from-orange-500 to-amber-500',
    },
    {
      label: 'Active Subscriptions',
      value: metrics?.activeSubscriptions || 0,
      icon: Users,
      color: 'from-pink-500 to-rose-500',
    },
  ];

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 ml-64">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-4xl font-bold flex items-center gap-3">
              <DollarSign className="w-10 h-10" />
              Financial Dashboard
            </h1>
            <p className="text-green-100 mt-2">
              Track revenue streams and financial metrics
            </p>
          </div>
        </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {revenueCards.map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200"
            >
              <div
                className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${card.color} mb-4`}
              >
                <card.icon className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">
                {card.value}
              </div>
              <div className="text-sm text-gray-600">{card.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-green-600" />
            Recent Transactions
          </h2>

          {transactions.length === 0 ? (
            <p className="text-center text-gray-600 py-12">No transactions found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">User</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Type</th>
                    <th className="text-left py-3 px-4 font-bold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 font-bold text-gray-700">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction: any) => (
                    <tr
                      key={transaction.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition"
                    >
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {transaction.user?.full_name || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500">{transaction.user?.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {transaction.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {transaction.description}
                      </td>
                      <td
                        className={`py-3 px-4 text-right text-sm font-bold ${
                          transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {transaction.amount > 0 ? '+' : ''}
                        {transaction.amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}
